/**
 * Explaining an access set (#5358 PR 15): for a hub, one of its agents and optionally a person, the
 * read and write sets every hub toolset call of that agent sends for that person, and, for each value
 * of each dimension, the layers that removed it.
 *
 *   GET /api/setup/hubs/:id/agents/:agentId/access-explain[?user_id=]
 *
 * It is worked out by the same functions a toolset call's set is (`apps/backend/src/rekor/access-set.ts`),
 * from the same layers, each read from the DO that owns it, so it cannot drift from what is sent, but
 * for one exception: explaining someone else matches no unredeemed team invite by their email
 * (`accessExplainQuery`), so a person holding one is shown without it.
 *   - with a person (`user_id`, or a person's own sign-in asking of themselves): their kind, first match
 *     wins (`ACCESS_PERSON_KINDS`, `./access-dimensions`), that kind's values ∩ the hub's ∩ the agent's: what a turn of theirs in
 *     their own conversation, on a channel that vouches for them, gets (that exception aside);
 *   - with no person (a scoped API token asking, with no `user_id`): the hub's ∩ the agent's, what a hub
 *     automation's turn of the agent gets.
 * A turn in another person's conversation is cut to that person's set too, and a delegated task's to the
 * set its delegation stored and its originators' current ones: those turns are not explained here.
 * The data product holds a call to its set only on record types that enforce a dimension (an `x-access`
 * entry in `enforce` mode); a type in `report` mode is not narrowed by it.
 *
 * **The persons** (wayai-pro/wayai#5473 §3, §4; `persons`): beside the set, the persons the agent's calls are
 * for in each turn shape the explanation covers (`ACCESS_EXPLAIN_TURNS`), worked out from the same read by
 * the functions a call's persons are (`turnPersonsOf`, `boundPersonOf`): each person-bound party's person set
 * (its ids, or `*`), the audience, and whether the call has full reach. A person's id is shown only to an
 * org admin of the hub's org; anyone else sees how many ids each party is bound to (`person_ids` null). A
 * person id that cannot be derived (the person-id secret missing) answers a 503, never a raw user id.
 */

import { z } from 'zod';
import { USER_ID_REGEX } from '../common';
import type { AccessAssignment, AccessPersonKind, AccessSet } from './access-dimensions';
import type { RekorBoundPerson } from '../rekor-toolset';

/**
 * The layers a turn's set is the intersection of, in the order they are intersected. An explanation of a
 * hub, an agent and a person names `hub`, the person's kind (`hub_public` for everyone else: the hub's
 * public values) and `agent`; the rest bound turns with more parties: a consult's agent initiator
 * (`initiator_agent`), a delegated task's stored set (`delegation`) and its originators' current sets
 * (`originator`), and a background agent's host turn (`host_agent`).
 */
export const ACCESS_LAYERS = [
  'hub',
  'org_member',
  'hub_admin',
  'hub_team_member',
  'hub_user',
  'hub_public',
  'initiator_agent',
  'delegation',
  'originator',
  'host_agent',
  'agent',
] as const;
export type AccessLayer = (typeof ACCESS_LAYERS)[number];

/**
 * Whose set to explain: a platform account. Absent: the caller's own, or none for a caller that is no
 * person's interactive sign-in (a scoped API token). Someone else's unredeemed team invites are not
 * matched by their email: naming any account would otherwise tell whether its email is one an invite on
 * the hub names.
 */
export const accessExplainQuery = z.object({
  user_id: z.string().regex(USER_ID_REGEX).optional(),
});

/** One layer of the set, as it was intersected. */
export interface AccessExplainLayer {
  layer: AccessLayer;
  /**
   * The values it holds. An unset layer reaches nothing and shows as `{}`; null is an agent's with no
   * values, which narrows nothing.
   */
  values: AccessAssignment | null;
}

/** A value a set misses, and every layer whose list misses it. */
export interface AccessExplainRemoval {
  value_id: string;
  layers: AccessLayer[];
}

/** One dimension of the org's, or one a layer names. */
export interface AccessExplainDimension {
  dimension_id: string;
  /** The set's lists, as sent; both empty when the set leaves the dimension out: it reaches nothing there. */
  read: string[];
  write: string[];
  /** Each value of the dimension (the org's, retired ones included, and any a layer names) read misses. */
  read_removed: AccessExplainRemoval[];
  /** Each value read reaches that write misses, with the layers whose write lists miss it. */
  write_removed: AccessExplainRemoval[];
}

export interface AccessExplain {
  hub_id: string;
  /** The production hub whose values apply: the hub itself, its production hub, or null on a preview never published. */
  values_hub_id: string | null;
  agent_id: string;
  /** Whose set it is; null: no person, so the hub's ∩ the agent's. */
  user_id: string | null;
  /** The person's kind on the hub, first match wins; null with no person. */
  kind: AccessPersonKind | null;
  /**
   * The access set every hub toolset call of the agent sends for the person; for someone else holding an
   * unredeemed team invite, the set without it (`accessExplainQuery`).
   */
  set: AccessSet;
  /**
   * The set is customer-only: the person is everyone else. An agent's call names no customer, and sends it as
   * it is, held to the record types open to customer-facing calls (wayai-pro/wayai#5473 §9, 2b1); a call naming a
   * customer is refused by the data product since it removed customer scope (2c). The set reaches nothing on every
   * other type that enforces a dimension.
   */
  customer_only: boolean;
  /**
   * The person's own set (the hub's ∩ their kind's values, before any agent's) reaches nothing. Every
   * turn in a conversation they are the person of is cut to it, whichever agent answers and a team
   * member's send or consult there included, so those reach nothing either.
   */
  conversations_reach_nothing: boolean;
  /** The layers intersected, in order. */
  layers: AccessExplainLayer[];
  dimensions: AccessExplainDimension[];
  /**
   * The persons the agent's calls are for (#5473 §3), per turn shape: with a person, `own_conversation` then
   * `team_send`; with none, `no_party`. Absent from a server from before it.
   */
  persons?: AccessExplainPersons[];
}

/**
 * The turn shapes an explanation's persons cover (#5473 §3 turn table):
 *   - `own_conversation`: the person's message in their own conversation. They are its customer, a person-bound
 *     party that never holds `*`;
 *   - `team_send`: the person's message with no customer in the turn (a team member's send, or a consult they
 *     start), where their `*` applies;
 *   - `no_party`: a turn for no one, in a conversation no person or team member ever wrote in (a hub automation's
 *     own), where only the agent's own `*` may apply.
 */
export const ACCESS_EXPLAIN_TURNS = ['own_conversation', 'team_send', 'no_party'] as const;
export type AccessExplainTurn = (typeof ACCESS_EXPLAIN_TURNS)[number];

/** One person-bound party of a turn, as the call names it. */
export interface AccessExplainPersonParty {
  /** The conversation's customer: never holds `*`. */
  customer: boolean;
  /** Holds `*` (`$person`): it passes every person-ruled record. */
  all: boolean;
  /** How many person ids it is bound to. None, and no `*`: proven as no one, so it passes no person-ruled record. */
  person_id_count: number;
  /** Its person ids, to an org admin of the hub's org; null to anyone else. */
  person_ids: string[] | null;
}

/** The persons the agent's calls are for in one turn shape (`ACCESS_EXPLAIN_TURNS`), as `X-Rekor-Bound-Person` names them. */
export interface AccessExplainPersons {
  turn: AccessExplainTurn;
  /** `staff` only when the turn is staff's (#5473 §4); anything else is customer-facing. */
  audience: RekorBoundPerson['audience'];
  /** Every person-bound party, in the turn's order. */
  parties: AccessExplainPersonParty[];
  /** The agent's own `*`: on a no-party turn of a staff audience only. */
  agent_all: boolean;
  /**
   * Full reach (#5473 §4): at least one person-bound party, every one holding `*`, or none and the agent's own
   * `*`. Not the audience: a customer-facing call is held apart whatever its reach.
   */
  full_reach: boolean;
}

export interface AccessExplainResponse {
  data: AccessExplain;
}
